import React, { useState } from 'react';
import { Calendar, BookOpen } from 'lucide-react';
import type { Article } from '../utils/types';
import { useFontSizes } from '../contexts/FontSizeContext';
import { useI18n } from '../i18n/I18nContext';
import { safeUrl, openInWorkingTab } from '../utils/navigate';

interface ArticleCardProps {
  article: Article;
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>();
function getDateFormatter(locale: string): Intl.DateTimeFormat {
  let fmt = dateFormatters.get(locale);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    dateFormatters.set(locale, fmt);
  }
  return fmt;
}

function formatDate(dateStr: string, locale = 'el-GR'): string {
  try {
    return getDateFormatter(locale).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

/** Max category/tag chips to show on an article tile. */
const MAX_CATEGORY_TAGS = 2;

export const ArticleCard = React.memo<ArticleCardProps>(function ArticleCard({ article }) {
  const { body, small } = useFontSizes();
  const { lang } = useI18n();
  const locale = lang === 'en' ? 'en-IE' : 'el-GR';
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = article.imageUrl && !imgFailed;

  // The feed packs every tag into a single comma-separated `category` string
  // (e.g. "Malagouzia, Greek grape varieties, wine"). Show only the first few
  // so the tile stays clean instead of rendering the whole tag list.
  const categoryTags = (article.category ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, MAX_CATEGORY_TAGS);

  return (
    <a
      href={safeUrl(article.url)}
      onClick={(e) => openInWorkingTab(article.url, e)}
      className={[
        'botilia-card flex gap-3 bg-white rounded-xl p-3',
        'border border-gray-100 hover:border-wine/20',
        'focus:outline-none focus:ring-2 focus:ring-wine/30 focus:ring-offset-2',
        'transition-all duration-200',
      ].join(' ')}
      aria-label={`${article.title}`}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-cream">
        {showImg ? (
          <img
            src={article.imageUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-wine/20">
            <BookOpen className="h-8 w-8" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Category tags (capped — feed joins all tags into one string) */}
        {categoryTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {categoryTags.map((tag) => (
              <span
                key={tag}
                className="inline-block text-[10px] font-semibold uppercase tracking-wider text-wine bg-wine/5 px-1.5 py-0.5 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className={`${body} font-semibold text-gray-900 leading-tight line-clamp-1`}>
          {article.title}
        </h3>

        {/* Excerpt */}
        <p className={`${small} text-gray-500 mt-0.5 line-clamp-2 leading-relaxed`}>
          {article.excerpt}
        </p>

        {/* Date */}
        <div className="flex items-center gap-1 mt-1.5 text-gray-400">
          <Calendar className="h-3 w-3" />
          <span className={small}>
            {formatDate(article.publishedAt, locale)}
          </span>
        </div>
      </div>
    </a>
  );
});
